import { __awaiter, __extends, __generator } from "tslib";
import { Batch } from "@pnp/odata";
import { Logger } from "@pnp/logging";
import { assign, jsS, isUrlAbsolute, hOP } from "@pnp/common";
import { GraphRuntimeConfig } from "./graphlibconfig";
import { GraphHttpClient } from "./graphhttpclient";
import { toAbsoluteUrl } from "./utils/toabsoluteurl";
var GraphBatch = /** @class */ (function (_super) {
    __extends(GraphBatch, _super);
    function GraphBatch(batchUrl, maxRequests) {
        if (batchUrl === void 0) { batchUrl = "v1.0/$batch"; }
        if (maxRequests === void 0) { maxRequests = 20; }
        var _this = _super.call(this) || this;
        _this.batchUrl = batchUrl;
        _this.maxRequests = maxRequests;
        return _this;
    }
    /**
     * Urls come to the batch absolute, but the processor expects relative
     * @param url Url to ensure is relative
     */
    GraphBatch.makeUrlRelative = function (url) {
        if (!isUrlAbsolute(url)) {
            // already not absolute, just give it back
            return url;
        }
        var index = url.indexOf(".com/v1.0/");
        if (index < 0) {
            index = url.indexOf(".com/beta/");
            if (index > -1) {
                // beta url
                return url.substr(index + 10);
            }
        }
        else {
            // v1.0 url
            return url.substr(index + 9);
        }
        // no idea
        return url;
    };
    GraphBatch.formatRequests = function (requests) {
        var _this = this;
        return requests.map(function (reqInfo, index) {
            var requestFragment = {
                id: "" + ++index,
                method: reqInfo.method,
                url: _this.makeUrlRelative(reqInfo.url),
            };
            var headers = {};
            // merge global config headers
            if (GraphRuntimeConfig.headers !== undefined && GraphRuntimeConfig.headers !== null) {
                headers = assign(headers, GraphRuntimeConfig.headers);
            }
            if (reqInfo.options !== undefined) {
                // merge per request headers
                if (reqInfo.options.headers !== undefined && reqInfo.options.headers !== null) {
                    headers = assign(headers, reqInfo.options.headers);
                }
                // all non-get requests need their own content-type header
                if (reqInfo.method !== "GET") {
                    headers["Content-Type"] = "application/json";
                }
                // add a request body
                if (reqInfo.options.body !== undefined && reqInfo.options.body !== null) {
                    // we need to parse the body which was previously turned into a string
                    requestFragment = assign(requestFragment, {
                        body: JSON.parse(reqInfo.options.body),
                    });
                }
            }
            requestFragment = assign(requestFragment, {
                headers: headers,
            });
            return requestFragment;
        });
    };
    GraphBatch.parseResponse = function (requests, graphResponse) {
        return new Promise(function (resolve, reject) {
            // we need to see if we have an error and report that
            if (hOP(graphResponse, "error")) {
                return reject(Error("Error Porcessing Batch: (" + graphResponse.error.code + ") " + graphResponse.error.message));
            }
            var parsedResponses = new Array(requests.length).fill(null);
            for (var i = 0; i < graphResponse.responses.length; ++i) {
                var response = graphResponse.responses[i];
                // we create the request id by adding 1 to the index, so we place the response by subtracting one to match
                // the array of requests and make it easier to map them by index
                var responseId = parseInt(response.id, 10) - 1;
                if (response.status === 204) {
                    parsedResponses[responseId] = new Response();
                }
                else {
                    parsedResponses[responseId] = new Response(JSON.stringify(response.body), response);
                }
            }
            resolve({
                nextLink: graphResponse.nextLink,
                responses: parsedResponses,
            });
        });
    };
    GraphBatch.prototype.executeImpl = function () {
        var _this = this;
        Logger.write("[" + this.batchId + "] (" + (new Date()).getTime() + ") Executing batch with " + this.requests.length + " requests.", 1 /* Info */);
        if (this.requests.length < 1) {
            Logger.write("Resolving empty batch.", 1 /* Info */);
            return Promise.resolve();
        }
        var client = new GraphHttpClient();
        // create a working copy of our requests
        var requests = this.requests.slice();
        var error = false;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var _loop_1, this_1, state_1;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _loop_1 = function () {
                            var requestsChunk, batchRequest, batchOptions, queryUrl;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        requestsChunk = requests.splice(0, this_1.maxRequests);
                                        batchRequest = {
                                            requests: GraphBatch.formatRequests(requestsChunk),
                                        };
                                        batchOptions = {
                                            body: jsS(batchRequest),
                                            headers: {
                                                "Accept": "application/json",
                                                "Content-Type": "application/json",
                                            },
                                            method: "POST",
                                        };
                                        Logger.write("[" + this_1.batchId + "] (" + (new Date()).getTime() + ") Sending batch request.", 1 /* Info */);
                                        return [4 /*yield*/, toAbsoluteUrl(this_1.batchUrl)];
                                    case 1:
                                        queryUrl = _a.sent();
                                        return [4 /*yield*/, client.fetch(queryUrl, batchOptions)
                                                .then(function (r) { return r.json(); })
                                                .then(function (j) { return GraphBatch.parseResponse(requestsChunk, j); })
                                                .then(function (parsedResponse) {
                                                Logger.write("[" + _this.batchId + "] (" + (new Date()).getTime() + ") Resolving batched requests.", 1 /* Info */);
                                                parsedResponse.responses.reduce(function (chain, response, index) {
                                                    var request = requestsChunk[index];
                                                    Logger.write("[" + _this.batchId + "] (" + (new Date()).getTime() + ") Resolving batched request " + request.method + " " + request.url + ".", 0 /* Verbose */);
                                                    return chain.then(function (_) { return request.parser.parse(response).then(request.resolve).catch(request.reject); });
                                                }, Promise.resolve());
                                            }).catch(function (e) {
                                                reject(e);
                                                error = true;
                                            })];
                                    case 2:
                                        _a.sent();
                                        if (error) {
                                            return [2 /*return*/, "break"];
                                        }
                                        return [2 /*return*/];
                                }
                            });
                        };
                        this_1 = this;
                        _a.label = 1;
                    case 1:
                        if (!(requests.length > 0)) return [3 /*break*/, 3];
                        return [5 /*yield**/, _loop_1()];
                    case 2:
                        state_1 = _a.sent();
                        if (state_1 === "break")
                            return [3 /*break*/, 3];
                        return [3 /*break*/, 1];
                    case 3:
                        if (!error) {
                            // if we didn't have an error go ahead and resolve the promise as successful
                            resolve();
                        }
                        return [2 /*return*/];
                }
            });
        }); });
    };
    return GraphBatch;
}(Batch));
export { GraphBatch };
//# sourceMappingURL=batch.js.map